import React, { useState, useCallback } from 'react';
import Cropper from 'react-easy-crop';

const AdUpload = ({ shopId, onUploadSuccess, onCancel }) => {
    const [imageSrc, setImageSrc] = useState(null);
    const [crop, setCrop] = useState({ x: 0, y: 0 });
    const [zoom, setZoom] = useState(1);
    const [croppedAreaPixels, setCroppedAreaPixels] = useState(null);
    const [uploading, setUploading] = useState(false);

    const onCropComplete = useCallback((croppedArea, croppedAreaPixels) => {
        setCroppedAreaPixels(croppedAreaPixels);
    }, []);

    const handleFileChange = (e) => {
        const file = e.target.files?.[0];
        if (file) {
            const reader = new FileReader();
            reader.addEventListener('load', () => {
                setImageSrc(reader.result);
            });
            reader.readAsDataURL(file);
        }
    };

    const createImage = (url) =>
        new Promise((resolve, reject) => {
            const image = new Image();
            image.addEventListener('load', () => resolve(image));
            image.addEventListener('error', (error) => reject(error));
            image.src = url;
        });

    const getCroppedImg = async (imageSrc, pixelCrop) => {
        const image = await createImage(imageSrc);
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        canvas.width = pixelCrop.width;
        canvas.height = pixelCrop.height;

        ctx.drawImage(
            image,
            pixelCrop.x,
            pixelCrop.y,
            pixelCrop.width,
            pixelCrop.height,
            0,
            0,
            pixelCrop.width,
            pixelCrop.height
        );

        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, 'image/png');
        });
    };

    const handleUpload = async () => {
        if (!croppedAreaPixels || !imageSrc) return;

        setUploading(true);
        try {
            const croppedImageBlob = await getCroppedImg(imageSrc, croppedAreaPixels);
            const file = new File([croppedImageBlob], `ad-${Date.now()}.png`, { type: 'image/png' });

            if (onUploadSuccess) {
                await onUploadSuccess(file);
            }

            setImageSrc(null);
            setCrop({ x: 0, y: 0 });
            setZoom(1);
        } catch (error) {
            console.error('Error uploading image:', error);
            alert('Failed to upload image');
        } finally {
            setUploading(false);
        }
    };

    if (!imageSrc) {
        return (
            <div style={{ padding: '20px', textAlign: 'center' }}>
                <h3>Upload Ad Banner</h3>
                <p style={{ color: '#666', marginBottom: '15px' }}>
                    Select an image to create an ad banner (16:9 ratio)
                </p>
                <input
                    type="file"
                    accept="image/*"
                    onChange={handleFileChange}
                    style={{ display: 'none' }}
                    id="ad-image-input"
                />
                <label htmlFor="ad-image-input">
                    <div style={{
                        display: 'inline-block',
                        padding: '12px 24px',
                        backgroundColor: '#4CAF50',
                        color: 'white',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        marginRight: '10px'
                    }}>
                        Choose Image
                    </div>
                </label>
                {onCancel && (
                    <button
                        onClick={onCancel}
                        style={{
                            padding: '12px 24px',
                            backgroundColor: '#666',
                            color: 'white',
                            border: 'none',
                            borderRadius: '4px',
                            cursor: 'pointer'
                        }}
                    >
                        Cancel
                    </button>
                )}
            </div>
        );
    }

    return (
        <div style={{ padding: '20px' }}>
            <h3>Crop Ad Banner</h3>
            <div style={{
                position: 'relative',
                width: '100%',
                height: '400px',
                backgroundColor: '#333',
                marginBottom: '20px'
            }}>
                <Cropper
                    image={imageSrc}
                    crop={crop}
                    zoom={zoom}
                    aspect={16 / 9}
                    onCropChange={setCrop}
                    onCropComplete={onCropComplete}
                    onZoomChange={setZoom}
                />
            </div>

            <div style={{ marginBottom: '20px' }}>
                <label style={{ display: 'block', marginBottom: '5px' }}>Zoom:</label>
                <input
                    type="range"
                    value={zoom}
                    min={1}
                    max={3}
                    step={0.1}
                    onChange={(e) => setZoom(e.target.value)}
                    style={{ width: '100%' }}
                />
            </div>

            <div style={{ display: 'flex', gap: '10px' }}>
                <button
                    onClick={handleUpload}
                    disabled={uploading}
                    style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: uploading ? '#ccc' : '#4CAF50',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: uploading ? 'not-allowed' : 'pointer',
                        fontSize: '16px'
                    }}
                >
                    {uploading ? 'Uploading...' : 'Upload Ad'}
                </button>
                <button
                    onClick={() => {
                        setImageSrc(null);
                        if (onCancel) onCancel();
                    }}
                    style={{
                        flex: 1,
                        padding: '12px',
                        backgroundColor: '#666',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px'
                    }}
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default AdUpload;
